import React from 'react';
import { 
  Phone, Mail, Star, User, IndianRupee, 
  Clock, Eye, Edit, Trash2, MessageSquare, RefreshCw, DollarSign, Target
} from 'lucide-react';

const LeadListView = ({ 
  leads, 
  onViewDetails, 
  onEditLead, 
  onUpdateLead, 
  onDeleteLead, 
  onAssignLead, 
  onGetPricing,
  getStageColor, 
  STAGE_OPTIONS, 
  TEMPERATURE_OPTIONS,
  selectedLeadIds = [],
  onToggleSelect,
  onToggleSelectAll,
  showSelection = false,
  hideHeader = false
}) => {
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-IN', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
    const timeStr = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata'
    });
    return `${dateStr}, ${timeStr}`;
  };

  const getTemperatureBadgeClass = (temp) => {
    const classes = {
      'hot': 'temp-badge-hot',
      'warm': 'temp-badge-warm',
      'cold': 'temp-badge-cold'
    };
    return classes[temp] || 'temp-badge-cold';
  };

  return (
    <div className="list-view-container">
      {/* Mobile Card View */}
      <div className="mobile-lead-cards">
        {leads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
            <MessageSquare size={48} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
            <p style={{ fontSize: '0.875rem' }}>No leads found</p>
          </div>
        ) : (
          leads.map(lead => (
            <div key={lead.id} className="mobile-lead-card">
              {/* Header with Name and Stage */}
              <div className="mobile-lead-card-header">
                <div style={{ flex: 1 }}>
                  <div className="mobile-lead-name">{lead.name}</div>
                  {lead.contact_attempts > 0 && (
                    <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Phone size={10} /> {lead.contact_attempts} attempts
                    </div>
                  )}
                </div>
                <span 
                  className="mobile-lead-stage" 
                  style={{ backgroundColor: getStageColor(lead.stage) }}
                >
                  {lead.stage === 'cancelled' && lead.is_duplicate 
                    ? 'Duplicate' 
                    : lead.stage === 'cancelled' && lead.is_refunded
                    ? 'Refund'
                    : (STAGE_OPTIONS.find(s => s.value === lead.stage)?.label || lead.stage)
                  }
                </span>
              </div>

              {/* Contact Details */}
              <div className="mobile-lead-details">
                {lead.email && lead.email !== 'noemail@placeholder.com' && (
                  <div className="mobile-lead-detail-row">
                    <Mail size={14} />
                    <span className="mobile-lead-detail-value">{lead.email}</span>
                  </div>
                )}
                <div className="mobile-lead-detail-row">
                  <Phone size={14} />
                  <span className="mobile-lead-detail-value">{lead.phone}</span>
                </div>
                {(lead.service_type || lead.destination_country || lead.display_country) && (
                  <div className="mobile-lead-detail-row">
                    <Target size={14} />
                    <span className="mobile-lead-detail-value">
                      {lead.service_type || 'N/A'} {(lead.display_country || lead.destination_country) && `• ${lead.display_country || lead.destination_country}`}
                    </span>
                  </div>
                )}
                {lead.assigned_to && (
                  <div className="mobile-lead-detail-row">
                    <User size={14} />
                    <span className="mobile-lead-detail-value">{lead.assigned_to}</span>
                  </div>
                )}
                <div className="mobile-lead-detail-row">
                  <Clock size={14} />
                  <span className="mobile-lead-detail-value">{formatDate(lead.created_at)}</span>
                </div>
              </div>

              {/* Badges */}
              <div className="mobile-lead-badges">
                <span className="mobile-badge mobile-badge-source">
                  {lead.source ? lead.source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
                </span>
                <span className={`mobile-badge mobile-badge-temp-${lead.temperature || 'cold'}`}>
                  {TEMPERATURE_OPTIONS.find(t => t.value === lead.temperature)?.label || lead.temperature}
                </span>
                {lead.announcement_id && (
                  <span className="mobile-badge" style={{ background: '#f3e8ff', color: '#7c3aed' }}>
                    🎯 Promo Lead
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="mobile-lead-actions">
                <button 
                  className="mobile-lead-action-btn primary"
                  onClick={() => onViewDetails(lead)}
                >
                  <Eye size={14} />
                  <span>View</span>
                </button>
                <button 
                  className="mobile-lead-action-btn"
                  onClick={() => onEditLead(lead)}
                  style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                >
                  <Edit size={14} />
                  <span>Edit</span>
                </button>
                {onAssignLead && (
                  <button 
                    className="mobile-lead-action-btn success"
                    onClick={() => onAssignLead(lead)}
                  >
                    <User size={14} />
                    <span>Assign</span>
                  </button>
                )}
                {onUpdateLead && (
                  <button 
                    className="mobile-lead-action-btn"
                    onClick={() => onUpdateLead(lead)}
                    style={{ 
                      background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                      color: 'white',
                      borderColor: '#7D6882'
                    }}
                  >
                    <RefreshCw size={14} />
                    <span>Update</span>
                  </button>
                )}
                {onGetPricing && lead.service_type && lead.destination_country && (
                  <button 
                    className="mobile-lead-action-btn"
                    onClick={() => onGetPricing(lead)}
                    style={{ 
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      color: 'white',
                      borderColor: '#047857',
                      fontWeight: '700'
                    }}
                  >
                    <DollarSign size={14} />
                    <span>Price</span>
                  </button>
                )}
                {onDeleteLead && (
                  <button 
                    className="mobile-lead-action-btn danger"
                    onClick={() => onDeleteLead(lead)}
                  >
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="list-view-table-wrapper">
        <table className="list-view-table">
          {!hideHeader && (
            <thead>
              <tr>
                {showSelection && (
                  <th className="list-th" style={{ width: '50px' }}>
                    <input 
                      type="checkbox" 
                      checked={leads.length > 0 && selectedLeadIds.length === leads.length}
                      onChange={onToggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                <th className="list-th sticky-col">Lead</th>
                <th className="list-th">Contact</th>
                <th className="list-th">Service</th>
                <th className="list-th">Source</th>
                <th className="list-th">Temperature</th>
                <th className="list-th">Stage</th>
                <th className="list-th">Assigned To</th>
                <th className="list-th">Created</th>
                <th className="list-th list-th-actions">Actions</th>
              </tr>
            </thead>
          )}
          <tbody>
            {leads.length === 0 ? (
              <tr>
                <td colSpan={showSelection ? "10" : "9"} className="list-empty">
                  <div className="list-empty-content">
                    <MessageSquare size={48} />
                    <p>No leads found</p>
                  </div>
                </td>
              </tr>
            ) : (
              leads.map(lead => (
                <tr key={lead.id} className="list-row">
                  {showSelection && (
                    <td className="list-td" style={{ width: '50px' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedLeadIds.includes(lead.id)}
                        onChange={() => onToggleSelect(lead.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                  )}
                  <td className="list-td sticky-col" data-label="Lead">
                    <div className="list-lead-info">
                      <div className="list-lead-name">{lead.name}</div>
                      {lead.contact_attempts > 0 && (
                        <div className="list-lead-meta">
                          <Phone size={11} /> {lead.contact_attempts} attempts
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="list-td" data-label="Contact">
                    <div className="list-contact">
                      {lead.email && lead.email !== 'noemail@placeholder.com' && (
                        <div className="list-contact-item">
                          <Mail size={13} /> {lead.email}
                        </div>
                      )}
                      <div className="list-contact-item">
                        <Phone size={13} /> {lead.phone}
                      </div>
                    </div>
                  </td>
                  
                  <td className="list-td" data-label="Service">
                    <div className="list-service">
                      <div>{lead.service_type || 'N/A'}</div>
                      {(lead.display_country || lead.destination_country) && (
                        <div className="list-service-sub">{lead.display_country || lead.destination_country}</div>
                      )}
                    </div>
                  </td>
                  
                  <td className="list-td" data-label="Source">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div className="list-source-badge" style={{fontSize: '0.75rem'}}>
                        {lead.source ? lead.source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
                      </div>
                      {lead.announcement_id && lead.announcement_title && (
                        <div 
                          style={{
                            position: 'relative',
                            display: 'inline-flex',
                            cursor: 'pointer'
                          }}
                          title={`Promo: ${lead.announcement_title}`}
                        >
                          <Target 
                            size={16} 
                            style={{ 
                              color: '#8b5cf6',
                              strokeWidth: 2.5
                            }} 
                          />
                          <div 
                            style={{
                              position: 'absolute',
                              bottom: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              marginBottom: '8px',
                              padding: '8px 12px',
                              background: '#1f2937',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              opacity: 0,
                              pointerEvents: 'none',
                              transition: 'opacity 0.2s',
                              zIndex: 1000,
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                            className="promo-tooltip"
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#a78bfa' }}>
                              🎯 Promo Lead
                            </div>
                            <div>{lead.announcement_title}</div>
                            <div 
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: '6px solid #1f2937'
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="list-td" data-label="Temperature">
                    <span className={`list-temp-badge ${getTemperatureBadgeClass(lead.temperature)}`}>
                      {TEMPERATURE_OPTIONS.find(t => t.value === lead.temperature)?.label || lead.temperature}
                    </span>
                  </td>
                  
                  <td className="list-td" data-label="Stage">
                    <span className="list-stage-badge" style={{ backgroundColor: getStageColor(lead.stage) }}>
                      {lead.stage === 'cancelled' && lead.is_duplicate 
                        ? 'Duplicate' 
                        : lead.stage === 'cancelled' && lead.is_refunded
                        ? 'Refund'
                        : (STAGE_OPTIONS.find(s => s.value === lead.stage)?.label || lead.stage)
                      }
                    </span>
                  </td>
                  
                  <td className="list-td" data-label="Assigned To">
                    <div className="list-assigned">
                      {lead.assigned_to ? (
                        <>
                          <User size={13} /> {lead.assigned_to}
                        </>
                      ) : (
                        <span className="list-unassigned">Unassigned</span>
                      )}
                    </div>
                  </td>
                  
                  <td className="list-td" data-label="Created">
                    <div className="list-date">
                      <Clock size={13} /> {formatDate(lead.created_at)}
                    </div>
                  </td>
                  
                  <td className="list-td list-td-actions" data-label="Actions">
                    <div className="list-actions">
                      <button 
                        className="list-action-btn view-btn" 
                        onClick={() => onViewDetails(lead)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      {onAssignLead && (
                        <button 
                          className="list-action-btn" 
                          onClick={() => onAssignLead(lead)}
                          title="Assign Lead"
                          style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          <User size={14} />
                        </button>
                      )}
                      {onUpdateLead && (
                        <button 
                          className="list-action-btn" 
                          onClick={() => onUpdateLead(lead)}
                          title="Update Lead"
                          style={{
                            background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                      {onGetPricing && lead.service_type && lead.destination_country && (
                        <button 
                          className="list-action-btn" 
                          onClick={() => onGetPricing(lead)}
                          title="Get Best Offer Price"
                          style={{
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                            color: 'white',
                            border: 'none',
                            fontSize: '14px',
                            fontWeight: '700'
                          }}
                        >
                          ₹
                        </button>
                      )}
                      <button 
                        className="list-action-btn edit-btn" 
                        onClick={() => onEditLead(lead)}
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadListView;

// Add CSS for tooltip hover effect
const style = document.createElement('style');
style.textContent = `
  .promo-tooltip {
    opacity: 0 !important;
    pointer-events: none !important;
  }
  [title]:hover .promo-tooltip {
    opacity: 1 !important;
  }
  
  /* Fix tooltip clipping - allow overflow on Source column */
  .list-td:nth-child(4) {
    overflow: visible !important;
    position: relative;
  }
  
  /* Ensure table row doesn't clip the tooltip */
  .list-row {
    position: relative;
  }
`;
if (!document.querySelector('style[data-promo-tooltip]')) {
  style.setAttribute('data-promo-tooltip', 'true');
  document.head.appendChild(style);
}
